import React, {useState} from 'react';
import { Edit2 } from 'lucide-react';
import {Address} from "@eshop/types/location.types.ts";
import {Button, Card, Space, Typography, Input, Row} from "antd";
import {EditOutlined, EnvironmentOutlined, InfoCircleOutlined, PhoneOutlined} from "@ant-design/icons";
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';

const { Text, Paragraph } = Typography;

interface AddressDetailsProps {
    index?: number | string;
    address?: Address;
    onEdit?: () => void;
    className?: string;
    style?: React.CSSProperties;
    onEditNote?: (note: string) => void;
}

const themedCardCommon = {
    style: {
        // Fondo/texto del contenedor Card
        background: 'var(--bg-default)',
        color: 'var(--text-default)',
      boxShadow: '0 0 8px 8px var(--border-default)',
    } as React.CSSProperties,
    styles: {
        // Fondo/texto del header y body de la Card
        header: {
            background: 'var(--bg-default)',
            color: 'var(--text-default)',
        },
        body: {
            background: 'var(--bg-default)',
            color: 'var(--text-default)',
        },
    } as any,   // eslint-disable-line @typescript-eslint/no-explicit-any
};

export const StandardAddressDetails:React.FC<AddressDetailsProps> = ({ index, address, onEdit, className, style }) => {
    if (!address) return null;

    const fullAddress = [
        address.street,
        address.addr_detail,
    ].filter(Boolean).join(', ');

    const fullCity = [
        address.verbose?.city || address.city,
        address.verbose?.state || address.state,
        address.verbose?.country || address.country,
    ].filter(Boolean).join(', ');

    return (
        <Card id={address.id?.toString() || index?.toString() }
            title={
                <Space>
                    <EnvironmentOutlined />
                    <Text strong style={{ color: 'var(--text-default)' }}>
                        {`${address.first_name} ${address.last_name}`}
                    </Text>
                </Space>
            }
            extra={<Button icon={<EditOutlined />} onClick={onEdit}>Editar</Button>}
            style={{ maxWidth: 400, ...themedCardCommon.style, ...style }}
            styles={themedCardCommon.styles}
            className={className}
        >
            <Space orientation="vertical" size="small">
                <Paragraph style={{ color: 'var(--text-default)', marginBottom: 0 }}>{fullAddress}</Paragraph>
                <Paragraph style={{ color: 'var(--text-default)', marginBottom: 0 }}>{fullCity}</Paragraph>
                <Paragraph style={{ color: 'var(--text-default)', marginBottom: 0 }}>
                    {address.verbose?.postcode || address.postcode}
                </Paragraph>
                {address.phone_number &&
                    <Space>
                        <PhoneOutlined />
                        <Text style={{ color: 'var(--text-default)' }}>{address.phone_number as string}</Text>
                    </Space>}
                {address.notes && (
                    <Space align="start">
                        <InfoCircleOutlined style={{ marginTop: 4 }} />
                        {/* mantenemos "secondary" para contraste suave */}
                        <Paragraph type="secondary" style={{ margin: 0 }}>{address.notes}</Paragraph>
                    </Space>
                )}
            </Space>
        </Card>
    );
}

export const ModernAddressDetails:React.FC<AddressDetailsProps> = ({
   index,
   address,
   onEdit,
   className,
   style,
   onEditNote,
}) => {
    const [editNoteMode, setEditNoteMode] = useState(false);
    const [noteText, setNoteText] = useState(address?.notes || '');
    const { resolvedTheme } = useThemeMode();

    const boxShadow = `0.125rem 0.125rem 0.25rem ${resolvedTheme === 'dark' ? 'rgba(200, 200, 200, 0.35)' : 'rgba(0, 0, 0, 0.35)'}`;

    if (!address) return null;

    const fullAddress = [
        address.street,
        address.addr_detail,
        address.verbose?.city || address.city,
        address.verbose?.state || address.state,
        address.verbose?.country || address.country,
    ].filter(Boolean).join(', ');

    const handleSaveNote = () => {
        onEditNote?.(noteText);
        setEditNoteMode(false);
    }

    return (
        <div
            id={address.id?.toString() || index?.toString() }
            style={{ maxWidth: 400, ...style }}
            className={className}
        >
            <Card
                className="border-0" /*shadow-sm */
                styles={{
                    header: { background: 'var(--bg-default)', color: 'var(--text-default)' },
                    body: { padding: '1.5rem', color: 'var(--text-default)', },
                }}
                style={{ maxWidth: '400px', margin: '0 auto', background: 'var(--bg-default)', color: 'var(--text-default)', boxShadow: boxShadow }}
            >
                <div className="d-flex justify-content-between align-items-start mb-3" style={{ color: 'var(--text-default)' }}>
                    <Text strong className="fs-5 h6 mt-auto mb-0" style={{ color: 'var(--text-default)' }}>
                        {`${address.first_name} ${address.last_name}`}
                    </Text>
                    {onEdit &&
                        <Button onClick={onEdit} type='link'>
                            <Edit2 size={18}/>
                        </Button>}
                </div>
                <Space orientation="vertical" size="small" className="w-100">
                    <div>
                        <EnvironmentOutlined className="me-2 mr-2 text-muted" />
                        <Text style={{ color: 'var(--text-default)' }}>
                            {fullAddress} [{address.verbose?.postcode || address.postcode}]
                        </Text>
                    </div>
                    {address.phone_number &&
                    <div>
                        <PhoneOutlined className="me-2 mr-2 text-muted" />
                        <Text style={{ color: 'var(--text-default)' }}>{address.phone_number as string}</Text>
                    </div>
                    }

                    {editNoteMode ? (
                        <div className="mt-2">
                            <Input.TextArea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                className="mb-2"
                                placeholder="Agregar aclaración..."
                                style={{
                                    background: 'var(--bg-default)',
                                    color: 'var(--text-default)',
                                }}
                            />
                            <Row className="justify-content-center">
                                <Button type="dashed" size="small"
                                        className="me-4 mr-4 rounded-pill px-4 py-0"
                                        onClick={() => setEditNoteMode(false)}>Cancelar</Button>
                                <Button size="small" className="rounded-pill px-4 py-0 hover_info hover_border_0" onClick={handleSaveNote}>Guardar</Button>
                            </Row>
                        </div>
                    ) : address.notes ? (
                        <div
                            className="d-flex mt-2 p-2 fst-italy font-italic"
                            style={{
                                background: 'var(--bg-default)',
                                color: 'var(--text-default)',
                                border: '1px solid rgba(127,127,127,0.25)',
                                borderRadius: 8,
                                padding: 8,
                            }}
                        >
                            <InfoCircleOutlined className="me-2 mr-2 text-warning" />
                            <Text className="w-100 flex-grow" style={{ color: 'var(--text-default)' }}>
                                {address.notes}
                            </Text>
                            {onEditNote &&
                                <Button
                                    type="text"
                                    size="small"
                                    className="border-0 hover_info hover_border_0"
                                    onClick={() => setEditNoteMode(true)}
                                >
                                    <Edit2 size={16}/>
                                </Button>}
                        </div>
                    ) : ( onEditNote &&
                            <Row>
                                <Button type="dashed" onClick={() => setEditNoteMode(true)} className="mt-2 mx-auto">
                                    Agregar aclaración
                                </Button>
                            </Row>
                    )}
                </Space>
            </Card>
        </div>
    );
}

export const ClasiccAddressDetails:React.FC<AddressDetailsProps> = ({ index, address, onEdit, className, style }) => {
    if (!address) return null;

    const fullAddress = [
        address.street,
        address.addr_detail,
    ].filter(Boolean).join(', ');

    const fullCity = [
        address.verbose?.city || address.city,
        address.verbose?.state || address.state,
        address.verbose?.country || address.country,
    ].filter(Boolean).join(', ');

    return (
        <Card id={address.id?.toString() || index?.toString() }
            title={
                <Space>
                    <EnvironmentOutlined />
                    <Text strong style={{ color: 'var(--text-default)' }}>
                        {`${address.first_name} ${address.last_name}`}
                    </Text>
                </Space>
            }
            extra={<Button icon={<EditOutlined />} onClick={onEdit}>Editar</Button>}
            style={{ maxWidth: 400, ...themedCardCommon.style, ...style }}
            styles={themedCardCommon.styles}
            className={className}
        >
            <Space orientation="vertical" size="small">
                <Paragraph style={{ color: 'var(--text-default)', marginBottom: 0 }}>{fullAddress}</Paragraph>
                <Paragraph style={{ color: 'var(--text-default)', marginBottom: 0 }}>{fullCity}</Paragraph>
                <Paragraph style={{ color: 'var(--text-default)', marginBottom: 0 }}>
                    {address.verbose?.postcode || address.postcode}
                </Paragraph>
                {address.phone_number &&
                    <Space>
                        <PhoneOutlined />
                        <Text style={{ color: 'var(--text-default)' }}>{address.phone_number as string}</Text>
                    </Space>}
                {address.notes && (
                    <Space align="start">
                        <InfoCircleOutlined style={{ marginTop: 4 }} />
                        <Paragraph type="secondary" style={{ margin: 0 }}>{address.notes}</Paragraph>
                    </Space>
                )}
            </Space>
        </Card>
    );
}